import { CommonButton, DatePicker, Loading, Modal } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TGiftPackType } from '../../../entities/giftPackages/queries';
import { TShipmentType } from '../../../entities/shipments/queries';
import { usePrintShipmentLabelMutation } from '../../../generated/graphql';
import { PrintLabelModal } from '../PrintLabelModal/PrintLabelModal';
import styles from './PrintMultipleLabelModal.module.scss';
import { ShipmentCustomsTable } from './ShipmentCustomsTable';

type TPrintMultipleLabelModalProps = {
  shipments: TShipmentType[];
  onCancel: () => void;
  onSuccess: () => void;
  defaultPackages: TGiftPackType[];
};

export const PrintMultipleLabelModal = (props: TPrintMultipleLabelModalProps) => {
  const { shipments, onCancel, onSuccess, defaultPackages } = props;

  const [shipmentsToUpdate, setShipmentsToUpdate] = useState<TShipmentType[]>(shipments);
  const [actualShippingDate, setActualShippingDate] = useState<Date>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);

  const headline = useMemo(() => {
    return `Print Label for ${shipments.length} Shipments`;
  }, [shipments.length]);

  const supportingText = useMemo(() => {
    return `Set the actual shipping date and customs data for the ${shipments.length} shipments`;
  }, [shipments.length]);

  const [printLabel] = usePrintShipmentLabelMutation();

  const isFormCompleted = useMemo(() => {
    if (!actualShippingDate) return false;
    if (!shipmentsToUpdate.length) return false;
    for (const shipment of shipmentsToUpdate) {
      const isCanadian = shipment.address?.country === 'CAN';
      if (isCanadian) continue;
      const hasCustoms = shipment.customsData && shipment.customsData?.length;
      if (!hasCustoms) return false;
    }
    return true;
  }, [shipmentsToUpdate, actualShippingDate]);

  const onSubmit = useCallback(async () => {
    if (!actualShippingDate) return toast.error('There is no Shipping Date set.');
    if (!isFormCompleted) return toast.error('There are international shipments with incomplete customs information.');
    setIsLoading(true);
    let recordId: string | undefined;
    try {
      for (const shipment of shipmentsToUpdate) {
        recordId = shipment.id;
        await printLabel({
          variables: {
            id: shipment.id,
            companyId: shipment.companyId,
            version: shipment._version,
            input: { actualShippingDate: format(actualShippingDate, 'yyyy-MM-dd'), customsData: shipment.customsData }
          }
        });
      }
      onSuccess?.();
    } catch (error) {
      console.error('Shipment label creation failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error printing the shipment labels.');
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess, printLabel, actualShippingDate, shipmentsToUpdate, isFormCompleted]);

  const bottomComponent = useCallback(() => {
    if (!shipments) return <></>;
    return (
      <div className={styles.topComponent}>
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='close' label='Cancel' disabled={isLoading} onClick={onCancel} variant='outlined' />
        <CommonButton className={clsx(styles.addBtn)} leadingIcon='check' label='Print Label' disabled={isLoading} onClick={onSubmit} variant='filled' />
        {isLoading && <Loading className={clsx(styles.loading)} />}
      </div>
    );
  }, [shipments, onCancel, onSubmit, isLoading]);

  return (
    <Modal headline={headline} size='large' supportingText={supportingText} closeModal={onCancel} showCustomButton={true} customButton={bottomComponent}>
      <div className={styles.modalTopComponents}>
        <DatePicker
          label='Shipping Date'
          name='actualShippingDate'
          className={clsx(styles.datePicker)}
          value={actualShippingDate}
          onChange={(v) => {
            setActualShippingDate(v != null ? (v as Date) : undefined);
          }}
        />
        <CommonButton className={clsx(styles.addButton)} label='Set Customs for ALL' onClick={() => setOpenModal(true)} variant='filled' />
      </div>
      <div className={clsx(styles.shipmentsContainer)}>
        {shipmentsToUpdate.map((s) => {
          return <ShipmentCustomsTable key={s.id} shipment={s} setShipmentsToUpdate={setShipmentsToUpdate} defaultPackages={defaultPackages} />;
        })}
      </div>
      {openModal && <PrintLabelModal onCancel={() => setOpenModal(false)} onSuccess={() => setOpenModal(false)} defaultPackages={defaultPackages} setAllMultiple={true} setShipmentsToUpdate={setShipmentsToUpdate} />}
    </Modal>
  );
};
